/* File: src\components\dashboard\DailyPerformanceChart.js */
/* File: src/components/dashboard/DailyPerformanceChart.js - Timezone Fix Applied */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    Label,
} from 'recharts';
import { formatCombinedDifficulty } from '../../contexts/gameHelpers';
// Keep existing date-fns utils if needed elsewhere, but primary logic uses date-fns-tz
import {
    // isDateToday, // Not used directly for filtering anymore
    // isDateYesterday, // Not used directly for filtering anymore
    // isSameDay, // Might be used if weekly/monthly logic relies on it
} from '../../utils/dateUtils'; // Keep existing utils path

// --- Base date-fns functions (Keep necessary ones) ---
import {
    startOfDay,
    endOfDay,
    startOfWeek,
    endOfWeek,
    startOfMonth,
    endOfMonth,
    addDays,
    subDays,
    addWeeks,
    subWeeks,
    addMonths,
    subMonths,
    format, // Keep for non-tz formatting if used
    getDay, // Keep for week aggregation if needed
    getDate, // Keep for month aggregation if needed
    getDaysInMonth, // Keep for month aggregation if needed
    isValid, // Keep for safe parsing
    parseISO, // Needed for safeParseDate
} from 'date-fns';

// --- Import date-fns-tz functions ---
import {
    toZonedTime,
    fromZonedTime,
    format as formatTz // Use alias to avoid conflict with date-fns format
} from 'date-fns-tz';


// --- Constants for Charting ---
const DEFAULT_TIMEZONE = 'UTC'; // Fallback timezone
const WEEK_STARTS_ON = 1; // 1 for Monday

// --- Helper: Aggregate Level for a Day (Remains unchanged) ---
const aggregateDailyLevel = (sessionsForDay) => {
    if (!sessionsForDay || sessionsForDay.length === 0) return null;
    let totalLevel = 0; let validCount = 0;
    sessionsForDay.forEach(session => {
        if (typeof session.nBack === 'number' && typeof session.difficulty === 'number') {
            const level = session.nBack + Math.max(0, Math.min(0.99, session.difficulty));
            totalLevel += level; validCount++;
        }
    });
    return validCount > 0 ? totalLevel / validCount : null;
};

// --- Helper: Safe Date Parsing (Remains unchanged) ---
const safeParseDate = (dateInput) => {
    if (!dateInput) return null;
    try {
        const date = typeof dateInput === 'string' ? parseISO(dateInput) : new Date(dateInput);
        return isValid(date) ? date : null;
    } catch (e) {
        console.warn(`Error parsing date: ${dateInput}`, e);
        return null;
    }
};


// --- Helper: Prepare Data for Chart (Handles all periods with Timezone) ---
const prepareChartData = (sessions, period, timezone) => {
    const tz = timezone && typeof timezone === 'string' && timezone.length > 0 ? timezone : DEFAULT_TIMEZONE; // Use provided timezone or default
    const nowInTz = toZonedTime(new Date(), tz); // Get 'now' in the target timezone

    if (!Array.isArray(sessions)) return { chartData: [], yDomain: [0, 5], xAxisProps: {}, tooltipFormatter: () => null };

    // Pre-filter and map sessions with valid UTC dates ONCE
    const processedSessions = sessions
        .map(s => {
            if (!s || !s.date) return null;
            const utcDate = safeParseDate(s.date); // Parse stored date string/object to get UTC Date object
            if (!utcDate) {
                 // console.warn(`Invalid date found for session ${s.id}: ${s.date}`); // Reduced verbosity
                 return null;
            }
            // Add level calculation here to avoid repetition later
            const level = (typeof s.nBack === 'number' && typeof s.difficulty === 'number')
                          ? s.nBack + Math.max(0, Math.min(0.99, s.difficulty))
                          : null;
            return { ...s, utcDate, level }; // Add the validated UTC date object and calculated level
        })
        .filter(s => s !== null && s.level !== null); // Remove nulls and sessions missing valid level

    let chartData = [];
    let allLevels = [];
    let xAxisProps = {}; // Keep label here for the nested <Label> component
    let tooltipFormatter = (label) => label; // Default formatter returns the label

    // --- Period Logic ---
    switch (period) {
        // --- TODAY vs YESTERDAY (Session Number X-Axis) ---
        case 'today': {
            // Calculate UTC boundaries based on the user's timezone
            const startOfTodayTZ = startOfDay(nowInTz);
            const endOfTodayTZ = endOfDay(nowInTz);
            const startOfTodayUTC = fromZonedTime(startOfTodayTZ, tz);
            const endOfTodayUTC = fromZonedTime(endOfTodayTZ, tz);

            const yesterdayInTz = subDays(nowInTz, 1);
            const startOfYesterdayTZ = startOfDay(yesterdayInTz);
            const endOfYesterdayTZ = endOfDay(yesterdayInTz);
            const startOfYesterdayUTC = fromZonedTime(startOfYesterdayTZ, tz);
            const endOfYesterdayUTC = fromZonedTime(endOfYesterdayTZ, tz);

            // Filter using UTC boundaries and the session's utcDate
            const todaySessions = processedSessions
                .filter(s => s.utcDate >= startOfTodayUTC && s.utcDate <= endOfTodayUTC)
                .sort((a, b) => a.utcDate - b.utcDate); // Sort by original time

            const yesterdaySessions = processedSessions
                .filter(s => s.utcDate >= startOfYesterdayUTC && s.utcDate <= endOfYesterdayUTC)
                .sort((a, b) => a.utcDate - b.utcDate); // Sort by original time

            // Map data for chart
            const maxSessionCount = Math.max(todaySessions.length, yesterdaySessions.length);
            if (maxSessionCount > 0) {
                for (let i = 0; i < maxSessionCount; i++) {
                    const sessionNumber = i + 1;
                    const todaySession = todaySessions[i];
                    const yesterdaySession = yesterdaySessions[i];

                    const todayLevel = todaySession?.level ?? null;
                    const yesterdayLevel = yesterdaySession?.level ?? null;

                    if (todayLevel !== null) allLevels.push(todayLevel);
                    if (yesterdayLevel !== null) allLevels.push(yesterdayLevel);

                    // Pass original UTC dates to tooltip payload for potential time display
                    chartData.push({
                        xValue: sessionNumber,
                        currentValue: todayLevel,
                        previousValue: yesterdayLevel,
                        currentOriginalDate: todaySession?.utcDate ?? null,
                        previousOriginalDate: yesterdaySession?.utcDate ?? null
                    });
                }
            }
            const xTicksToday = maxSessionCount > 0 ? Array.from({ length: Math.min(maxSessionCount, 10) }, (_, i) => Math.round((i + 1) * (maxSessionCount / Math.min(maxSessionCount, 10)))) : [1]; // Show up to 10 ticks
            if(maxSessionCount > 0 && !xTicksToday.includes(1)) xTicksToday.unshift(1); // Ensure 1 is always shown
            if(maxSessionCount > 1 && !xTicksToday.includes(maxSessionCount)) xTicksToday.push(maxSessionCount); // Ensure max is shown
             // Prevent duplicate ticks if maxSessionCount is small
            const uniqueTicks = [...new Set(xTicksToday.filter(t => t > 0))].sort((a, b) => a - b);


            xAxisProps = { dataKey: 'xValue', type: 'number', allowDecimals: false, domain: [1, Math.max(1, maxSessionCount)], label: { value: "Session #", position: 'insideBottom', offset: -15, fontSize: 11, fill: '#6b7280' }, ticks: uniqueTicks };
             // Tooltip formatter uses the timezone passed to CustomTooltip
            tooltipFormatter = (label) => `Session #${label}`; // Label itself is enough
            break;
        }

        // --- THIS WEEK vs LAST WEEK (Day of Week X-Axis) ---
        case 'week': {
            // Calculate UTC boundaries for weeks based on the user's timezone
            const currentWeekStartTZ = startOfWeek(nowInTz, { weekStartsOn: WEEK_STARTS_ON });
            const currentWeekEndTZ = endOfWeek(nowInTz, { weekStartsOn: WEEK_STARTS_ON });
            const previousWeekStartTZ = startOfWeek(subWeeks(nowInTz, 1), { weekStartsOn: WEEK_STARTS_ON });
            const previousWeekEndTZ = endOfWeek(subWeeks(nowInTz, 1), { weekStartsOn: WEEK_STARTS_ON });

            const currentWeekStartUTC = fromZonedTime(currentWeekStartTZ, tz);
            const currentWeekEndUTC = fromZonedTime(currentWeekEndTZ, tz);
            const previousWeekStartUTC = fromZonedTime(previousWeekStartTZ, tz);
            const previousWeekEndUTC = fromZonedTime(previousWeekEndTZ, tz);

            // Filter sessions for the correct UTC week ranges first
            const currentWeekSessionsAll = processedSessions.filter(s => s.utcDate >= currentWeekStartUTC && s.utcDate <= currentWeekEndUTC);
            const previousWeekSessionsAll = processedSessions.filter(s => s.utcDate >= previousWeekStartUTC && s.utcDate <= previousWeekEndUTC);

            // Aggregate per day
            for (let i = 0; i < 7; i++) {
                const currentDayTargetTZ = addDays(currentWeekStartTZ, i); // Day in user's timezone
                const previousDayTargetTZ = addDays(previousWeekStartTZ, i);

                // Calculate UTC boundaries for the specific day
                const dayStartCurrentUTC = fromZonedTime(startOfDay(currentDayTargetTZ), tz);
                const dayEndCurrentUTC = fromZonedTime(endOfDay(currentDayTargetTZ), tz);
                const dayStartPreviousUTC = fromZonedTime(startOfDay(previousDayTargetTZ), tz);
                const dayEndPreviousUTC = fromZonedTime(endOfDay(previousDayTargetTZ), tz);

                // Filter the week's sessions for the specific day using UTC boundaries
                const currentDaySessions = currentWeekSessionsAll.filter(s => s.utcDate >= dayStartCurrentUTC && s.utcDate <= dayEndCurrentUTC);
                const previousDaySessions = previousWeekSessionsAll.filter(s => s.utcDate >= dayStartPreviousUTC && s.utcDate <= dayEndPreviousUTC);

                const currentLevel = aggregateDailyLevel(currentDaySessions);
                const previousLevel = aggregateDailyLevel(previousDaySessions);

                if (currentLevel !== null) allLevels.push(currentLevel);
                if (previousLevel !== null) allLevels.push(previousLevel);

                const dayName = formatTz(currentDayTargetTZ, 'EEE', { timeZone: tz }); // Format using TZ date

                chartData.push({
                    xValue: dayName, // Use day name for X-axis category
                    dayDate: currentDayTargetTZ, // Store the ZONED date for the tooltip formatter
                    currentValue: currentLevel,
                    previousValue: previousLevel
                 });
            }
            // Ensure correct order based on WEEK_STARTS_ON (data is already generated in order)
            xAxisProps = { dataKey: 'xValue', type: 'category', label: { value: "Day of Week", position: 'insideBottom', offset: -15, fontSize: 11, fill: '#6b7280' } };
             // Tooltip formatter uses the timezone passed to CustomTooltip
            tooltipFormatter = (label, payload) => {
                const point = payload?.[0]?.payload;
                const dateStr = point?.dayDate ? formatTz(point.dayDate, 'MMM d', { timeZone: tz }) : '';
                return `${label} (${dateStr})`;
            };
            break;
        }

        // --- THIS MONTH vs LAST MONTH (Day of Month X-Axis) ---
        case 'month': {
            // Calculate UTC boundaries for months based on the user's timezone
            const currentMonthStartTZ = startOfMonth(nowInTz);
            const currentMonthEndTZ = endOfMonth(nowInTz);
            const previousMonthStartTZ = startOfMonth(subMonths(nowInTz, 1));
            const previousMonthEndTZ = endOfMonth(subMonths(nowInTz, 1));

            const currentMonthStartUTC = fromZonedTime(currentMonthStartTZ, tz);
            const currentMonthEndUTC = fromZonedTime(currentMonthEndTZ, tz);
            const previousMonthStartUTC = fromZonedTime(previousMonthStartTZ, tz);
            const previousMonthEndUTC = fromZonedTime(previousMonthEndTZ, tz);

            // Filter sessions for the correct UTC month ranges first
            const currentMonthSessionsAll = processedSessions.filter(s => s.utcDate >= currentMonthStartUTC && s.utcDate <= currentMonthEndUTC);
            const previousMonthSessionsAll = processedSessions.filter(s => s.utcDate >= previousMonthStartUTC && s.utcDate <= previousMonthEndUTC);

            // Aggregate per day of the month
            const daysInCurrentMonth = getDaysInMonth(nowInTz); // Use TZ date

            for (let i = 1; i <= daysInCurrentMonth; i++) {
                 const currentDayTargetTZ = addDays(currentMonthStartTZ, i - 1); // Day in user's timezone
                 const previousDayTargetTZ = addDays(previousMonthStartTZ, i - 1);
                 const previousDayExists = previousDayTargetTZ <= previousMonthEndTZ; // Check using TZ dates

                 // Calculate UTC boundaries for the specific day
                 const dayStartCurrentUTC = fromZonedTime(startOfDay(currentDayTargetTZ), tz);
                 const dayEndCurrentUTC = fromZonedTime(endOfDay(currentDayTargetTZ), tz);

                 const currentDaySessions = currentMonthSessionsAll.filter(s => s.utcDate >= dayStartCurrentUTC && s.utcDate <= dayEndCurrentUTC);
                 const currentLevel = aggregateDailyLevel(currentDaySessions);
                 if (currentLevel !== null) allLevels.push(currentLevel);

                 let previousLevel = null;
                 if (previousDayExists) {
                     const dayStartPreviousUTC = fromZonedTime(startOfDay(previousDayTargetTZ), tz);
                     const dayEndPreviousUTC = fromZonedTime(endOfDay(previousDayTargetTZ), tz);
                     const previousDaySessions = previousMonthSessionsAll.filter(s => s.utcDate >= dayStartPreviousUTC && s.utcDate <= dayEndPreviousUTC);
                     previousLevel = aggregateDailyLevel(previousDaySessions);
                     if (previousLevel !== null) allLevels.push(previousLevel);
                 }

                 chartData.push({
                     xValue: i, // Day number
                     dayDate: currentDayTargetTZ, // Store the ZONED date for the tooltip formatter
                     currentValue: currentLevel,
                     previousValue: previousLevel
                 });
             }
             // Show ticks for every few days, plus first and last
            const tickInterval = Math.max(1, Math.floor(daysInCurrentMonth / 10)); // Aim for ~10 ticks
            const xTicksMonth = chartData
                .map(d => d.xValue)
                .filter((day, idx) => day === 1 || day === daysInCurrentMonth || (day % tickInterval === 0))
                .filter((day, index, self) => self.indexOf(day) === index); // Ensure uniqueness

            xAxisProps = { dataKey: 'xValue', type: 'number', allowDecimals: false, domain: [1, Math.max(1, daysInCurrentMonth)], label: { value: "Day of Month", position: 'insideBottom', offset: -15, fontSize: 11, fill: '#6b7280' }, ticks: xTicksMonth };
             // Tooltip formatter uses the timezone passed to CustomTooltip
             tooltipFormatter = (label, payload) => {
                 const point = payload?.[0]?.payload;
                 const dateStr = point?.dayDate ? formatTz(point.dayDate, 'MMM d', { timeZone: tz }) : '';
                 return `${dateStr}`; // Just the date
             };
            break;
        }
        default:
            console.error(`Invalid period type: ${period}`);
            return { chartData: [], yDomain: [0, 5], xAxisProps: {}, tooltipFormatter: () => null };
    }

    // --- Calculate Y-Axis Domain (Remains unchanged) ---
    let yDomain = [0, 5]; // Default Y-axis
     if (allLevels.length > 0) {
        const minY = Math.min(...allLevels);
        const maxY = Math.max(...allLevels);
        if (minY === maxY) { yDomain = [Math.max(0, minY - 0.2), maxY + 0.2]; }
        else { yDomain = [Math.max(0, minY - 0.1), maxY + 0.1]; }
        if (yDomain[1] - yDomain[0] < 0.5) { const mid = (yDomain[0] + yDomain[1]) / 2; yDomain = [Math.max(0, mid - 0.25), mid + 0.25]; }
    }

    return { chartData, yDomain, xAxisProps, tooltipFormatter };
};


// --- Tooltip Formatter (Accepts timezone prop) ---
const CustomTooltip = ({ active, payload, label, formatter, period, timezone }) => {
    if (active && payload && payload.length) {
        const currentData = payload.find(p => p.dataKey === 'currentValue');
        const previousData = payload.find(p => p.dataKey === 'previousValue');

        // The formatter function itself might use the timezone for week/month
        const periodLabel = typeof formatter === 'function' ? formatter(label, payload) : label;

        // Keep original level formatting
        const formatLevel = (levelValue) => {
            if (levelValue === null || levelValue === undefined) return 'N/A';
            const nBack = Math.floor(levelValue);
            const difficulty = levelValue - nBack;
            return formatCombinedDifficulty(nBack, difficulty);
        };

        const currentSeriesName = period === 'today' ? 'Today' : 'Current Period';
        const previousSeriesName = period === 'today' ? 'Yesterday' : 'Previous Period';

        // Find original UTC dates from payload if stored previously
        const pointData = payload[0]?.payload;
        const currentOriginalDate = pointData?.currentOriginalDate; // This is a UTC Date object
        const previousOriginalDate = pointData?.previousOriginalDate; // This is a UTC Date object

        return (
            <div className="bg-white border border-gray-300 shadow-lg rounded-md p-3 text-xs">
                <p className="font-semibold mb-1">{periodLabel}</p> {/* Shows "Session #X" or "Mon (Jun 10)" etc */}
                {currentData && (
                    <p style={{ color: currentData.color }}>
                        {`${currentSeriesName}: ${formatLevel(currentData.value)}`}
                        {/* Optionally display formatted time using timezone for 'today' chart */}
                        {currentOriginalDate && period === 'today' && timezone && (
                            <span className="text-gray-500 ml-1 text-[10px]">
                                ({formatTz(currentOriginalDate, 'HH:mm', { timeZone: timezone })})
                            </span>
                        )}
                    </p>
                )}
                {previousData && (
                    <p style={{ color: previousData.color }}>
                        {`${previousSeriesName}: ${formatLevel(previousData.value)}`}
                         {/* Optionally display formatted time using timezone for 'today' chart */}
                         {previousOriginalDate && period === 'today' && timezone && (
                            <span className="text-gray-500 ml-1 text-[10px]">
                                ({formatTz(previousOriginalDate, 'HH:mm', { timeZone: timezone })})
                            </span>
                        )}
                    </p>
                )}
            </div>
        );
    }
    return null;
};


// --- Main Chart Component (Accepts timezone prop) ---
const DailyPerformanceChart = ({ sessions, selectedPeriod, timezone }) => {
    const { chartData, yDomain, xAxisProps, tooltipFormatter } = useMemo(
        // Pass timezone to prepareChartData
        () => prepareChartData(sessions, selectedPeriod, timezone),
        [sessions, selectedPeriod, timezone] // Add timezone dependency
    );

    // Keep original destructuring for label prop if it exists
    const { label: xAxisLabelConfig, ...restXAxisProps } = xAxisProps;

    const hasData = chartData && chartData.length > 0 && chartData.some(d => d.currentValue !== null || d.previousValue !== null);

    // Keep original names and colors
    const currentLineName = selectedPeriod === 'today' ? 'Today' : 'Current Period';
    const previousLineName = selectedPeriod === 'today' ? 'Yesterday' : 'Previous Period';
    const currentLineColor = "#4f46e5";
    const previousLineColor = "#a0aec0";

    if (!hasData) {
        // Keep original no data rendering
        return (
            <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm text-center h-72 sm:h-80 flex flex-col justify-center items-center">
                 <div className="w-12 h-12 mx-auto mb-3 bg-gray-100 rounded-full flex items-center justify-center text-gray-400">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
                    </svg>
                </div>
                <p className="text-gray-500 text-sm">No relevant session data found for the selected period.</p>
                <p className="text-xs text-gray-400 mt-1">
                    Complete more sessions to display data.
                </p>
            </div>
        );
    }

    return (
        // Keep original container div
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 h-72 sm:h-80">
            <ResponsiveContainer width="100%" height="100%">
                <LineChart
                    data={chartData}
                    margin={{ top: 5, right: 20, left: 0, bottom: 20 }} // Keep original margins
                >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    {/* Keep original XAxis config, using destructured props */}
                    <XAxis
                        {...restXAxisProps} // Spread props except label config
                        tick={{ fontSize: 10, fill: '#6b7280' }}
                        padding={{ left: 10, right: 10 }}
                        interval="preserveStartEnd" // Ensure first/last ticks show for category axis
                        {...(restXAxisProps.type === 'number' && { tickCount: restXAxisProps.ticks?.length || 7 })} // Adjust tick count for numeric axis
                    >
                       {/* Render Label component using the config object */}
                       {xAxisLabelConfig && <Label {...xAxisLabelConfig} />}
                    </XAxis>
                    {/* Keep original YAxis config */}
                    <YAxis
                        type="number"
                        domain={yDomain}
                        allowDecimals={true}
                        tickFormatter={(tick) => tick.toFixed(2)} // Keep original formatter
                        tick={{ fontSize: 10, fill: '#6b7280' }}
                        width={45}
                    >
                         <Label value="N-Back Level" angle={-90} position="insideLeft" style={{ textAnchor: 'middle', fontSize: 11, fill: '#6b7280' }} offset={-5} />
                    </YAxis>
                    {/* Pass timezone prop to CustomTooltip */}
                    <Tooltip content={<CustomTooltip formatter={tooltipFormatter} period={selectedPeriod} timezone={timezone}/>} />
                    {/* Keep original Legend config */}
                    <Legend verticalAlign="top" height={30} wrapperStyle={{ fontSize: 11 }}/>
                    {/* Keep original Line configs */}
                    <Line
                        isAnimationActive={true} 
                        type="monotone"
                        dataKey="currentValue"
                        name={currentLineName}
                        stroke={currentLineColor}
                        strokeWidth={2}
                        connectNulls={false} // Do not connect lines over missing data points
                        dot={{ r: 4, strokeWidth: 1 }}
                        activeDot={{ r: 6, strokeWidth: 1, fill: currentLineColor }}
                    />
                    <Line
                        isAnimationActive={true}
                        type="monotone"
                        dataKey="previousValue"
                        name={previousLineName}
                        stroke={previousLineColor}
                        strokeWidth={2}
                        connectNulls={false} // Do not connect lines over missing data points
                        dot={{ r: 4, strokeWidth: 1 }}
                        activeDot={{ r: 6, strokeWidth: 1, fill: previousLineColor }}
                    />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
};

// Keep original PropTypes, add timezone
DailyPerformanceChart.propTypes = {
    sessions: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        date: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]).isRequired, // ISO string or Date obj
        nBack: PropTypes.number, // Allow null/undefined if calculation happens later
        difficulty: PropTypes.number, // Allow null/undefined
    })).isRequired,
    selectedPeriod: PropTypes.oneOf(['today', 'week', 'month']).isRequired,
    // Add timezone prop type
    timezone: PropTypes.string, // User's timezone string (e.g., 'America/New_York')
};

// Add default prop for timezone
DailyPerformanceChart.defaultProps = {
    timezone: DEFAULT_TIMEZONE // Use constant defined above
};

export default DailyPerformanceChart;